import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from '@shared/styles/components/FullScreenModal.module.css'
import designSystemStyles from "@shared/styles/designSystemStyles"
import { CloseButton } from "../Button"
import { HorizontalContainer } from "../Containers"
import { Text } from "../Texts"

export type FullScreenModalLayout = "floating" | "fullscreen"

interface FullScreenModalProps {
    children: React.ReactNode
    isOpen: boolean
    onClose: () => void
    title?: string
    /** rendered on the right side of the header, before the close button */
    headerAccessory?: React.ReactNode
    /** sticky footer content; omit to render no footer */
    footer?: React.ReactNode
    /** defaults to true on the floating layout and false on the fullscreen layout */
    closeOnEsc?: boolean
    /** "floating" (default) is inset with rounded corners; "fullscreen" is edge-to-edge */
    layout?: FullScreenModalLayout
    className?: string
}

export const FullScreenModal: React.FC<FullScreenModalProps> = ({
    children,
    isOpen,
    onClose,
    title = '',
    headerAccessory,
    footer,
    closeOnEsc,
    layout = "floating",
    className
}) => {
    const shouldCloseOnEsc = closeOnEsc ?? (layout === "floating")
    const closeAnimationMs = 180
    const [isVisible, setIsVisible] = useState(isOpen)
    const [isClosing, setIsClosing] = useState(false)
    const lastOpenChildrenRef = useRef<React.ReactNode>(children)

    const handleClose = useCallback(() => {
        onClose()
    }, [onClose])

    useEffect(() => {
        if (!isOpen || !shouldCloseOnEsc) {
            return
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                handleClose()
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [shouldCloseOnEsc, handleClose, isOpen])

    // Lock body scroll while open
    useEffect(() => {
        if (!isOpen) {
            return
        }
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true)
            setIsClosing(false)
        } else if (isVisible) {
            setIsClosing(true)
            const timeout = window.setTimeout(() => {
                setIsClosing(false)
                setIsVisible(false)
            }, closeAnimationMs)
            return () => window.clearTimeout(timeout)
        }
    }, [closeAnimationMs, isOpen, isVisible])

    if (!isVisible) return null
    if (isOpen) {
        lastOpenChildrenRef.current = children
    }
    const renderChildren = isOpen ? children : lastOpenChildrenRef.current

    return (
        <div
            className={[
                styles['fullscreen-backdrop'],
                isClosing ? styles['fullscreen-backdrop-closing'] : styles['fullscreen-backdrop-open'],
                'DSFullScreenModalBackdrop'
            ].join(' ')}
        >
            <div
                className={[
                    styles['fullscreen-container'],
                    layout === "fullscreen" ? styles['fullscreen-container-edge'] : '',
                    isClosing ? styles['fullscreen-container-closing'] : styles['fullscreen-container-open'],
                    'DSFullScreenModal',
                    className
                ].filter(Boolean).join(' ')}
                role="dialog"
                aria-modal="true"
                aria-label={title || undefined}
            >
                <div className={styles['fullscreen-header']}>
                    <HorizontalContainer
                        gap={designSystemStyles.gapNone}
                        width="100%"
                        justifyContent="space-between"
                        alignment="center"
                    >
                        <Text text={"**" + title.trim() + "**"} markdown={true} />
                        <HorizontalContainer
                            gap={designSystemStyles.gapMd}
                            alignment="center"
                            width="auto"
                            minWidth="fit-content"
                        >
                            {headerAccessory}
                            <CloseButton onClick={handleClose} />
                        </HorizontalContainer>
                    </HorizontalContainer>
                </div>
                <div className={styles['fullscreen-content']}>
                    {renderChildren}
                </div>
                {footer && (
                    <div className={styles['fullscreen-footer']}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    )
}
